import { useEffect, useState } from 'react';
import { AppLayout } from '../components/Layout/AppLayout';
import { useCredentials } from '../hooks/useCredentials';
import { useMembers } from '../hooks/useMembers';
import { useGroups } from '../hooks/useGroups';
import { useAuth } from '../hooks/useAuth';
import { KeyRound, Users, Shield, ArrowRight, Chrome, ExternalLink, Building2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CHROME_WEBSTORE_URL } from '../utils/constants';
import { workspacesApi } from '../api/workspaces';
import { CertificateExpiryBanner } from '../components/Certificates/CertificateExpiryBanner';
import { UsageKpis } from '../components/Dashboard/UsageKpis';
import { PageHeader } from '../components/common/PageHeader';
import { useBotaoAbrir } from '../utils/extensaoLictor';
import { formatNumber } from '../utils/formatters';
import { primeiroNome } from '../utils/nomes';

// M5: os dois avisos de configuração somem quando a pessoa dispensa. A chave do salva-senhas é a
// mesma do aviso da página de credenciais: dispensou num lugar, some nos dois.
const AVISO_EXTENSAO_KEY = 'lictor_dashboard_extension_dismissed';
const AVISO_SALVA_SENHAS_KEY = 'lictor_autofill_disclaimer_dismissed';

function lerDispensa(chave: string): boolean {
  try {
    return localStorage.getItem(chave) === 'true';
  } catch {
    return false;
  }
}

function gravarDispensa(chave: string) {
  try {
    localStorage.setItem(chave, 'true');
  } catch {
    // Sem localStorage (janela anônima restrita): o aviso só some até recarregar.
  }
}

export function Dashboard() {
  const { user, membership } = useAuth();
  const { credentials, fetchCredentials, isLoading: loadingCreds } = useCredentials();
  const { members, fetchMembers, isLoading: loadingMembers } = useMembers();
  const { groups, fetchGroups, isLoading: loadingGroups } = useGroups();
  const [workspacesCount, setWorkspacesCount] = useState<number | null>(null);
  // M5: o cartão da extensão só aparece se ela não respondeu (null = ainda perguntando).
  const extensao = useBotaoAbrir(true);
  const [extensaoDispensada, setExtensaoDispensada] = useState(() => lerDispensa(AVISO_EXTENSAO_KEY));
  const [salvaSenhasDispensado, setSalvaSenhasDispensado] = useState(() => lerDispensa(AVISO_SALVA_SENHAS_KEY));

  useEffect(() => {
    fetchCredentials();
    fetchMembers();
    if (membership?.role === 'admin' || membership?.role === 'owner') {
      fetchGroups();
    }
    workspacesApi.list()
      .then(data => setWorkspacesCount(data ? data.length : 0))
      .catch(() => setWorkspacesCount(0));
  }, [fetchCredentials, fetchMembers, fetchGroups, membership?.role]);

  // Cor única da marca nos ícones (design system: azul e roxo ficam reservados para estados).
  const stats: { label: string; value: number | null; icon: typeof KeyRound; link: string }[] = [
    { label: 'Credenciais ativas', value: loadingCreds ? null : credentials.length, icon: KeyRound, link: '/credentials' },
    { label: 'Membros da organização', value: loadingMembers ? null : members.length, icon: Users, link: '/members' },
    { label: 'Minhas organizações', value: workspacesCount, icon: Building2, link: '/organizations' },
  ];

  if (membership?.role === 'admin' || membership?.role === 'owner') {
    stats.push({ label: 'Grupos de acesso', value: loadingGroups ? null : groups.length, icon: Shield, link: '/groups' });
  }

  const nome = primeiroNome(user?.name) || primeiroNome(user?.email?.split('@')[0]);
  const mostrarExtensao = extensao === 'instalar' && !extensaoDispensada;
  const mostrarSalvaSenhas = !salvaSenhasDispensado;

  return (
    <AppLayout>
      <CertificateExpiryBanner />
      <PageHeader title={`Olá, ${nome}`} description={`Bem-vindo ao painel da ${membership?.org_name ?? 'sua organização'}.`} />

      {/* M4: quatro indicadores cabem numa linha mesmo com o menu aberto */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 'var(--space-4)' }}>
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.link}
              to={stat.link}
              className="glass animate-scale-in cartao-indicador"
              style={{
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                boxShadow: 'var(--shadow-md)',
                textDecoration: 'none',
                color: 'inherit',
                minWidth: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                <div style={{ padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', background: 'var(--color-highlight)', color: 'var(--color-primary-400)', display: 'inline-flex' }}>
                  <Icon size={20} />
                </div>
                {stat.value === null ? (
                  <span className="skeleton" style={{ width: 48, height: 32 }} aria-label="Carregando" />
                ) : (
                  <span style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(stat.value)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                <span>{stat.label}</span>
                <ArrowRight size={16} style={{ color: 'var(--color-primary-400)', flexShrink: 0 }} />
              </div>
            </Link>
          );
        })}
      </div>

      <UsageKpis />

      {mostrarExtensao && (
        <div className="glass animate-scale-in" style={{ marginTop: 'var(--space-8)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-md)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <Chrome size={22} style={{ color: 'var(--color-primary-400)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <h2 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-1)' }}>Instale a extensão do Lictor</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.5 }}>
              Para preencher suas credenciais e o 2FA automaticamente nos tribunais.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <a href={CHROME_WEBSTORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn--primary btn--sm" style={{ textDecoration: 'none' }}>
              Instalar na Chrome Web Store <ExternalLink size={14} />
            </a>
            <button type="button" className="botao-icone botao-icone--sem-borda" aria-label="Dispensar aviso da extensão" title="Dispensar" onClick={() => { gravarDispensa(AVISO_EXTENSAO_KEY); setExtensaoDispensada(true); }}>
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* M5: aviso curto; o passo a passo por navegador está na página de perguntas frequentes. */}
      {mostrarSalvaSenhas && (
        <div className="glass animate-scale-in" style={{ marginTop: 'var(--space-6)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-md)', borderLeft: '3px solid var(--color-warning)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <Shield size={20} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <h2 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-1)' }}>Desative o salva-senhas do navegador</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.5 }}>
              Evita senhas desatualizadas, preenchimento errado e bloqueio de conta nos tribunais. O Lictor deve ser a única fonte de credenciais.{' '}
              <Link to="/faq#salva-senhas" style={{ color: 'var(--color-primary-400)' }}>Ver o passo a passo</Link>
            </p>
          </div>
          <button type="button" className="botao-icone botao-icone--sem-borda" aria-label="Dispensar aviso do salva-senhas" title="Já fiz, dispensar" onClick={() => { gravarDispensa(AVISO_SALVA_SENHAS_KEY); setSalvaSenhasDispensado(true); }}>
            <X size={18} />
          </button>
        </div>
      )}

    </AppLayout>
  );
}
